import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { publicationApi, readPublicationErrorDetail } from '../api';
import { useInvalidateRegisterContext } from '../hooks/useRegisterContext';
import { PublicationRegisterImpact } from './PublicationRegisterImpact';
import { SEVERITY_COLORS } from './ReviewIssuesPanel';
import type {
  PublicationCandidate,
  PublicationConflict,
  PublicationValidation,
  PublicationWarning,
  ReviewPolicySeverity,
} from '../types';

/**
 * A conflict blocks publication only while it is unresolved; warnings are the
 * backend's non-blocking observations and read as flags.
 */
const severityOfConflict = (conflict: PublicationConflict): ReviewPolicySeverity =>
  conflict.blocking && !conflict.resolved ? 'BLOCKER' : 'ADVISORY';

/** Anything the resolver has to say about a publication, in the shape the rows render. */
type PublicationIssue = PublicationConflict | PublicationWarning;

type PanelState =
  | { kind: 'loading' }
  | { kind: 'ready'; candidate: PublicationCandidate }
  | { kind: 'blocked'; validation: PublicationValidation }
  | { kind: 'stale' }
  | { kind: 'success'; versionNumber: number; created: boolean }
  | { kind: 'error'; message: string };

interface PublishCheckPanelProps {
  docId: string | null;
  isOpen: boolean;
  /** This drawing's pnid_no — the identifier family the impact rows list files by. */
  currentDrawingNo: string | null;
  onClose: () => void;
  /** Notify the parent so it can render the published-version chip. */
  onPublished: (versionNumber: number) => void;
  showAlert: (
    title: string,
    message: string,
    type?: 'success' | 'error' | 'info' | 'warning',
  ) => void;
  /** Opens the Notes & Revisions drawer where conflicts get resolved. */
  onOpenNotes?: () => void;
}

/**
 * Checks a drawing against the publication rules, shows what publishing would do
 * to the plant register, and publishes it. Every failure answers with an object
 * `detail` carrying a code, so each branch here keys off that code.
 */
const PublishCheckPanel: React.FC<PublishCheckPanelProps> = ({
  docId,
  isOpen,
  currentDrawingNo,
  onClose,
  onPublished,
  showAlert,
  onOpenNotes,
}) => {
  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [publishing, setPublishing] = useState(false);
  const invalidateRegisterContext = useInvalidateRegisterContext();

  const loadCandidate = useCallback(async () => {
    if (!docId) return;
    setState({ kind: 'loading' });
    try {
      const candidate = await publicationApi.getCandidate(docId);
      setState({ kind: 'ready', candidate });
    } catch (error) {
      const detail = readPublicationErrorDetail(error);
      if (detail?.code === 'publication_blocked' && detail.validation) {
        setState({ kind: 'blocked', validation: detail.validation });
        return;
      }
      if (detail?.code === 'working_copy_stale' || detail?.code === 'publication_approval_drift') {
        setState({ kind: 'stale' });
        return;
      }
      if (detail?.code === 'publication_source_invalid') {
        setState({
          kind: 'error',
          message: detail.message || 'This drawing has no valid source file to publish from.',
        });
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setState({
          kind: 'error',
          message: 'You do not have permission to publish this drawing.',
        });
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setState({ kind: 'error', message: 'This drawing has nothing to publish yet.' });
        return;
      }
      console.error('Failed to load publication candidate:', error);
      setState({ kind: 'error', message: 'Could not run the publish check. Please try again.' });
    }
  }, [docId]);

  useEffect(() => {
    if (!isOpen || !docId) return;
    void loadCandidate();
  }, [isOpen, docId, loadCandidate]);

  const handlePublish = async (candidate: PublicationCandidate) => {
    if (!docId || publishing) return;
    setPublishing(true);
    try {
      const result = await publicationApi.publish(docId, {
        expected_updated_at: candidate.expected_updated_at,
        expected_data_sha256: candidate.data_sha256,
      });
      setState({
        kind: 'success',
        versionNumber: result.version_number,
        created: result.created,
      });
      if (result.created) {
        // The publish is what settles which drawing is primary, so the review
        // table's Primary column and the edit panel's locks must re-read it.
        invalidateRegisterContext(docId);
        onPublished(result.version_number);
        showAlert('Published', `Published as v${result.version_number}.`, 'success');
      } else {
        showAlert(
          'Already published',
          'This drawing is already published with no changes since the last version.',
          'info',
        );
      }
    } catch (error) {
      const detail = readPublicationErrorDetail(error);
      if (detail?.code === 'working_copy_stale' || detail?.code === 'publication_approval_drift') {
        setState({ kind: 'stale' });
      } else if (detail?.code === 'publication_blocked' && detail.validation) {
        setState({ kind: 'blocked', validation: detail.validation });
      } else if (detail?.code === 'publication_source_invalid') {
        setState({
          kind: 'error',
          message: detail.message || 'This drawing has no valid source file to publish from.',
        });
      } else {
        console.error('Publish failed:', error);
        setState({ kind: 'error', message: 'Publishing failed. Please try again.' });
      }
    } finally {
      setPublishing(false);
    }
  };

  if (!docId) {
    return <div style={{ fontSize: 12, color: '#6b7280' }}>No document selected.</div>;
  }

  if (state.kind === 'loading') {
    return <div style={{ fontSize: 12, color: '#6b7280' }}>Running publish check…</div>;
  }

  if (state.kind === 'error') {
    return (
      <div style={{ fontSize: 12 }}>
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
          }}>
          {state.message}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="button" onClick={() => void loadCandidate()} style={secondaryButtonStyle}>
            Retry
          </button>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'success') {
    return (
      <div style={{ fontSize: 12 }}>
        <div
          style={{
            padding: 14,
            borderRadius: 8,
            backgroundColor: state.created ? '#ecfdf3' : '#f9fafb',
            border: `1px solid ${state.created ? '#a7f3d0' : '#e5e7eb'}`,
            color: state.created ? '#047857' : '#374151',
            fontWeight: 600,
          }}>
          {state.created
            ? `Published as v${state.versionNumber}`
            : 'Already published — no changes'}
        </div>
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'stale') {
    return (
      <div style={{ fontSize: 12 }}>
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            fontWeight: 600,
          }}>
          The drawing changed since this check.
          <div style={{ marginTop: 4, fontWeight: 500, color: '#a16207' }}>
            Re-run the check to publish the current state.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            type="button"
            onClick={() => void loadCandidate()}
            style={primaryButtonStyle(false)}>
            Refresh check
          </button>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (state.kind === 'blocked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <PublicationConflictList validation={state.validation} />
        <div
          style={{
            color: '#6b7280',
            fontSize: 12,
            borderTop: '1px solid #e5e7eb',
            paddingTop: 12,
          }}>
          Resolve these in Notes &amp; Revisions before publishing.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onOpenNotes && (
            <button type="button" onClick={onOpenNotes} style={primaryButtonStyle(false)}>
              Open Notes &amp; Revisions
            </button>
          )}
          <button type="button" onClick={() => void loadCandidate()} style={secondaryButtonStyle}>
            Re-check
          </button>
        </div>
      </div>
    );
  }

  const { candidate } = state;
  const identity = candidate.data.document;
  const nextVersion = (candidate.latest_version ?? 0) + 1;
  // This candidate has no blocking conflict left — or it would not have loaded —
  // so its warnings are the only verdict still worth showing before the impact.
  const warnings = identity?.validation?.warnings ?? [];

  return (
    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
          {identity?.document_number || 'Untitled drawing'}
        </div>
        {identity?.document_title && (
          <div style={{ color: '#6b7280', marginTop: 2 }}>{identity.document_title}</div>
        )}
      </div>

      <div
        style={{
          padding: '8px 10px',
          borderRadius: 8,
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          color: '#1d4ed8',
          fontWeight: 700,
        }}>
        {candidate.latest_version === null
          ? 'First publication'
          : `v${candidate.latest_version} → v${nextVersion}`}
      </div>

      {warnings.length > 0 && (
        <div>
          <PublicationIssueGroup severity="FLAG" rows={warnings} />
          <div style={{ color: '#6b7280', marginTop: 6 }}>
            Publishing goes ahead — the register keeps a flag beside these.
          </div>
        </div>
      )}

      <PublicationRegisterImpact
        impacts={candidate.asset_impacts}
        note={candidate.note || undefined}
        hierarchyNote={candidate.hierarchy_note || undefined}
        currentDrawingNo={currentDrawingNo}
      />

      <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
        <button
          type="button"
          onClick={() => void handlePublish(candidate)}
          disabled={publishing}
          style={primaryButtonStyle(publishing)}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
        <button type="button" onClick={onClose} disabled={publishing} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </div>
  );
};

/** Severity pill counts over the grouped `{field, message}` rows of a blocked publication. */
const PublicationConflictList: React.FC<{ validation: PublicationValidation }> = ({
  validation,
}) => {
  const conflicts = validation.conflicts ?? [];
  const warnings = validation.warnings ?? [];
  const groups: Array<{ severity: ReviewPolicySeverity; rows: PublicationIssue[] }> = [
    { severity: 'BLOCKER', rows: conflicts.filter((c) => severityOfConflict(c) === 'BLOCKER') },
    { severity: 'FLAG', rows: warnings },
    { severity: 'ADVISORY', rows: conflicts.filter((c) => severityOfConflict(c) === 'ADVISORY') },
  ];

  return (
    <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {groups.map(({ severity, rows }) => {
          const colors = SEVERITY_COLORS[severity];
          return (
            <span
              key={severity}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                borderRadius: 999,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bg,
                color: colors.text,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}>
              <span
                style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: colors.dot }}
              />
              {rows.length} {severity.toLowerCase()}
            </span>
          );
        })}
      </div>

      {groups.map(({ severity, rows }) => (
        <PublicationIssueGroup key={severity} severity={severity} rows={rows} />
      ))}
    </div>
  );
};

/** One severity's `{field, message}` rows, in that severity's colors. */
const PublicationIssueGroup: React.FC<{
  severity: ReviewPolicySeverity;
  rows: PublicationIssue[];
}> = ({ severity, rows }) => {
  if (rows.length === 0) return null;
  const colors = SEVERITY_COLORS[severity];
  return (
    <div>
      <div style={{ ...sectionLabelStyle, color: colors.text }}>{severity}</div>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {rows.map((issue, index) => (
          <div
            key={`${issue.code}-${issue.field}-${index}`}
            style={{
              display: 'flex',
              gap: 8,
              padding: '7px 10px',
              borderTop: index === 0 ? 'none' : '1px solid #f3f4f6',
              backgroundColor: 'white',
              alignItems: 'baseline',
            }}>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                fontWeight: 700,
                color: colors.text,
                whiteSpace: 'nowrap',
              }}>
              {/* A warning about the drawing as a whole names no field. */}
              {issue.field || issue.code}
            </span>
            <span style={{ color: '#4b5563' }}>{issue.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 0.6,
  color: '#6b7280',
  marginBottom: 6,
};

const primaryButtonStyle = (busy: boolean): React.CSSProperties => ({
  borderRadius: 7,
  border: '1px solid #1d4ed8',
  backgroundColor: busy ? '#93c5fd' : '#2563eb',
  color: 'white',
  padding: '6px 14px',
  fontSize: 11,
  fontWeight: 700,
  cursor: busy ? 'wait' : 'pointer',
});

const secondaryButtonStyle: React.CSSProperties = {
  borderRadius: 7,
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: 'white',
  padding: '6px 14px',
  fontSize: 11,
  fontWeight: 600,
  color: '#334155',
  cursor: 'pointer',
};

export default PublishCheckPanel;
